"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import api, { channelsAPI, userAPI, handleApiError } from "../../../services/api";
import { getUserTypeColor } from "../../../utils/userTypeUtils";
import "./InviteMembersModal.css";

const InviteMembersModal = ({ isOpen, onClose, channelId, channelName, existingMemberIds = [] }) => {
  const { t } = useTranslation();
  const { getUserType, user: authUser } = useAuth();

  // Get user type color
  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);

  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load users using existing user search API
  useEffect(() => {
    const loadUsers = async () => {
      if (isOpen && searchQuery.trim()) {
        setIsLoading(true);
        try {
          // Use centralized user search API
          const response = await userAPI.searchUsers({
            query: searchQuery.trim(),
            exclude_channel_id: channelId // Exclude users already in channel
          });

          if (response.data && response.data.data) {
            // Filter out ADMIN and CLINIC_MODERATOR users from search results
            // CLINIC_MODERATOR users should only appear in clinic team management
            const filteredUsers = response.data.data.filter(
              user => {
                const uid = user.user_id || user.id;
                const isExistingMember = existingMemberIds.some(id => Number(id) === Number(uid));
                
                return (
                  !isExistingMember &&
                  user.user_type !== 'ADMIN' &&
                  user.user_type !== 'admin' &&
                  user.user_type !== 'CLINIC_MODERATOR' &&
                  user.user_type !== 'clinic_moderator'
                );
              }
            );

            const transformedUsers = filteredUsers.map(user => {
              const uid = user.user_id || user.id;
              const isSelf = uid === (authUser?.id || authUser?.user_id);
              const authUserName = isSelf ? (authUser?.name || [authUser?.first_name, authUser?.last_name].filter(Boolean).join(" ").trim() || authUser?.username) : null;
              
              const displayName = user.full_name || 
                                user.name || 
                                [user.first_name, user.last_name].filter(Boolean).join(" ").trim() || 
                                user.username || 
                                authUserName || 
                                "Unknown";
              return {
                id: uid,
                name: displayName,
                profileImage: user.profile_image_url || "/icons/person.png",
              };
            });

            setUsers(transformedUsers);
          }
        } catch (error) {
          handleApiError(error);
        } finally {
          setIsLoading(false);
        }
      } else if (isOpen && !searchQuery.trim()) {
        // Clear users when search is empty
        setUsers([]);
      }
    };

    loadUsers();
  }, [isOpen, searchQuery, channelId]);

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUserToggle = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleInvite = async () => {
    if (selectedUsers.length === 0) return;

    setIsSubmitting(true);

    try {
      const userIds = selectedUsers.map(user => user.id);
      await channelsAPI.inviteMembers(channelId, userIds);
      setSelectedUsers([]);
      setSearchQuery("");
      onClose();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="invite-members-modal-overlay" onClick={onClose}>
      <div className="invite-members-modal" onClick={(e) => e.stopPropagation()} style={{ '--user-type-color': userTypeColor }}>
        <div className="invite-members-modal-header">
          <div className="invite-members-modal-header-content">
            <h2 className="invite-members-modal-title">
              {t("inviteMembers.title", "Invite Members to {{name}}", {
                name: channelName,
              })}
            </h2>
            {selectedUsers.length > 0 && (
              <span className="invite-members-modal-subtitle">
                {selectedUsers.length}{" "}
                {selectedUsers.length === 1
                  ? t("inviteMembers.singleSelected", "user selected")
                  : t("inviteMembers.multiSelected", "users selected")}
              </span>
            )}
          </div>
          <button className="invite-members-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="invite-members-modal-content">
          <div className="invite-members-modal-body">
            <div className="invite-members-search">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder={t(
                  "inviteMembers.searchPlaceholder",
                  "Search users..."
                )}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="invite-members-search-input"
              />
            </div>

            {selectedUsers.length > 0 && (
              <div className="invite-members-selected">
                <div className="invite-members-selected-header">
                  <span>
                    {t("inviteMembers.selectedLabel", "Selected ({{count}})", {
                      count: selectedUsers.length,
                    })}
                  </span>
                  <button
                    className="invite-members-clear-btn"
                    onClick={() => setSelectedUsers([])}
                  >
                    {t("inviteMembers.clear", "Clear")}
                  </button>
                </div>
                <div className="invite-members-selected-list">
                  {selectedUsers.map(user => (
                    <div key={user.id} className="invite-members-selected-item">
                      <img src={user.profileImage || "/icons/person.png"} alt={user.name} />
                      <span>{user.name}</span>
                      <button onClick={() => handleUserToggle(user)}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="invite-members-users-list">
              {isLoading ? (
                <div className="invite-members-loading">
                  <div className="loading-spinner"></div>
                  <p>
                    {t("inviteMembers.loading", "Loading users...")}
                  </p>
                </div>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map(user => {
                  const isSelected = selectedUsers.some(u => u.id === user.id);
                  return (
                    <div
                      key={user.id}
                      className={`invite-members-user-item ${isSelected ? "selected" : ""}`}
                      onClick={() => handleUserToggle(user)}
                    >
                      <img
                        src={user.profileImage || "/icons/person.png"}
                        alt={user.name}
                        className="invite-members-user-avatar"
                        onError={(e) => {
                          e.target.src = "/icons/person.png";
                        }}
                      />
                      <div className="invite-members-user-info">
                        <span className="invite-members-user-name">{user.name}</span>
                      </div>
                      {isSelected && (
                        <div className="invite-members-user-check">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="invite-members-empty">
                  <p>
                    {t("inviteMembers.empty", "No users found")}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="invite-members-modal-actions">
            <button
              className="invite-members-modal-submit"
              onClick={handleInvite}
              disabled={selectedUsers.length === 0 || isSubmitting}
            >
              {isSubmitting
                ? t("inviteMembers.submitting", "Inviting...")
                : t("inviteMembers.submit", "Invite ({{count}})", {
                  count: selectedUsers.length,
                })}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default InviteMembersModal;

